import { useEffect, useState } from "react";
import { SectionCard } from "@/components/common/SectionCard";
import { ghostBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import {
  listManufacturingNamedVariantApplicationsForParent,
  listManufacturingNamedVariants,
  type NamedVariantApplicationSummary,
} from "@/lib/manufacturing/namedVariantsClient";
import { updateManufacturingRecipe } from "@/lib/manufacturing/recipesClient";

type NestedAppRow = NamedVariantApplicationSummary & {
  named_variant_code: string;
  named_variant_name: string;
  scope: "self" | "component";
};

/** Named variants on this recipe and its direct BOM components — Active = product default. */
export function ManufacturingModelsAppliedNamedVariantsStrip({
  parentRecipeId,
  componentRecipeIds,
  defaultNamedVariantApplicationIds,
  onOpenVariantsWorkspace,
  onDefaultsSaved,
}: {
  parentRecipeId: string | null;
  componentRecipeIds: readonly string[];
  defaultNamedVariantApplicationIds: readonly string[];
  onOpenVariantsWorkspace: (namedVariantId?: string) => void;
  onDefaultsSaved?: () => void;
}) {
  const [rows, setRows] = useState<NestedAppRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [activeIds, setActiveIds] = useState<string[]>(() => [...defaultNamedVariantApplicationIds]);

  const defaultsKey = defaultNamedVariantApplicationIds.join(",");
  const componentsKey = componentRecipeIds.join(",");

  useEffect(() => {
    setActiveIds(defaultsKey ? defaultsKey.split(",") : []);
  }, [defaultsKey]);

  useEffect(() => {
    if (!parentRecipeId) {
      setRows([]);
      return;
    }
    let cancelled = false;
    setBusy(true);
    setLocalError(null);

    const parentIds = [
      parentRecipeId,
      ...componentsKey.split(",").filter((id) => id && id !== parentRecipeId),
    ];
    const uniqueParents = [...new Set(parentIds)];

    void (async () => {
      const variantsR = await listManufacturingNamedVariants();
      const codeById = new Map<string, { code: string; name: string }>();
      if (variantsR.ok) {
        for (const v of variantsR.data.variants) {
          codeById.set(v.id, { code: v.code, name: v.name });
        }
      }

      const collected: NestedAppRow[] = [];
      for (const rid of uniqueParents) {
        const r = await listManufacturingNamedVariantApplicationsForParent(rid);
        if (!r.ok) continue;
        for (const app of r.data.applications) {
          const meta = codeById.get(app.named_variant_id);
          collected.push({
            ...app,
            named_variant_code: meta?.code ?? "VARIANT",
            named_variant_name: meta?.name ?? "Named variant",
            scope: rid === parentRecipeId ? "self" : "component",
          });
        }
      }
      collected.sort((a, b) => {
        const c = a.named_variant_code.localeCompare(b.named_variant_code);
        if (c !== 0) return c;
        return a.parent_recipe_code.localeCompare(b.parent_recipe_code);
      });
      if (cancelled) return;
      setBusy(false);
      setRows(collected);
    })();

    return () => {
      cancelled = true;
    };
  }, [parentRecipeId, componentsKey]);

  async function toggleActive(appId: string, nextActive: boolean) {
    if (!parentRecipeId) return;
    const next = nextActive
      ? [...new Set([...activeIds, appId])]
      : activeIds.filter((id) => id !== appId);
    setSavingId(appId);
    setLocalError(null);
    const r = await updateManufacturingRecipe({
      recipe_id: parentRecipeId,
      default_named_variant_application_ids: next,
    });
    setSavingId(null);
    if (!r.ok) {
      setLocalError(r.error);
      return;
    }
    setActiveIds(next);
    onDefaultsSaved?.();
  }

  if (!parentRecipeId) return null;

  return (
    <SectionCard
      title="Named variants"
      description="Upgrades available on this recipe or its BOM components. Active = included in this SKU’s standard COGS."
      rightSlot={
        <button type="button" className={ghostBtnClass} onClick={() => onOpenVariantsWorkspace()}>
          Open Variants
        </button>
      }
    >
      {busy ? <div className="text-sm text-gray-500">Loading…</div> : null}
      {localError ? <div className="mb-2 text-sm text-red-600 dark:text-red-400">{localError}</div> : null}
      {!busy && rows.length === 0 ? (
        <div className="text-sm text-gray-500 dark:text-zinc-400">
          No named variants on this recipe or its components. Use Variants → Apply to recipes.
        </div>
      ) : null}
      <ul className="space-y-1">
        {rows.map((app) => {
          const isActive = activeIds.includes(app.id);
          const confirmed = app.confirm_status === "confirmed";
          return (
            <li
              key={app.id}
              className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-zinc-800"
            >
              <button
                type="button"
                className="min-w-0 flex-1 text-left"
                onClick={() => onOpenVariantsWorkspace(app.named_variant_id)}
              >
                <span className="font-medium text-gray-900 dark:text-zinc-100">
                  {app.confirm_status === "pending" ? (
                    <span className="mr-1 font-bold text-amber-700 dark:text-amber-300">!</span>
                  ) : null}
                  {app.named_variant_code}
                </span>
                <span className="mt-0.5 block truncate text-xs text-gray-500 dark:text-zinc-400">
                  {app.named_variant_name}
                  {app.scope === "component"
                    ? ` · on ${app.parent_recipe_code || "component"}`
                    : " · on this recipe"}
                  {app.included_by_default ? " · component default-on" : ""}
                  {" · "}
                  {app.confirm_status}
                </span>
              </button>
              <label className="flex shrink-0 items-center gap-1.5 text-xs text-gray-600 dark:text-zinc-300">
                <input
                  type="checkbox"
                  className="rounded border-gray-300"
                  checked={isActive}
                  disabled={!confirmed || savingId === app.id}
                  onChange={(e) => void toggleActive(app.id, e.target.checked)}
                />
                Active
              </label>
            </li>
          );
        })}
      </ul>
    </SectionCard>
  );
}
